import LRU from 'lru-cache';
import { toRefs, reactive, computed, unref, watch } from 'vue';
import type { AxiosResponse, AxiosPromise, AxiosError } from '@libs/http';
import { useCallback } from './useCallback';

export type UseQueryConfig<T> = {
  key?: any[];
  staleTime?: number;
  cacheTime?: number;
  immediate?: boolean | ((...arg: any[]) => boolean);
  onReject?: (...arg: any[]) => void;
  onSuccess?: (result: State<T>['result'], ...arg: any[]) => void;
  onSettled?: (...arg: any[]) => void;
};

type State<T> = {
  status: number;
  result: AxiosResponse<T> | null;
  error: any;
};

export const CACHE = new LRU<string, any>({ max: 1024 });

export const READY_STATUS = {
  IDLE: 1,
  PENDING: 2,
  SUCCESS: 3,
  FAILED: 4,
  REVALIDATING: 5,
};

const BASE_STATE_MODEL = {
  status: READY_STATUS.IDLE,
  result: null,
  error: null,
} as const;

function parseQueryKey(key: any[]): string {
  return key.map((keyItem) => unref(keyItem)).join('-');
}

function getReactiveValues(key: any[]): any[] {
  return key.filter((item) => unref(item) !== item);
}

export function useQuery<T>(
  fetcher: (...arg: any[]) => AxiosPromise<T>,
  {
    key = [],
    staleTime = 1000, // 1s
    cacheTime = 1000 * 60 * 60, // 1h

    immediate = true,

    onReject = () => {},
    onSuccess = () => {},
    onSettled = () => {},
  }: UseQueryConfig<T> = {}
) {
  if (typeof key !== 'function' && !Array.isArray(key)) {
    throw new Error('Key should be an array or a function');
  }

  const onKeyUpdated = useCallback();
  const state: State<T> = reactive({
    ...BASE_STATE_MODEL,
  });

  const reactiveValuesInKey = getReactiveValues(key);
  const shouldFetch = computed(() =>
    typeof immediate === 'function' ? immediate(state) : unref(immediate)
  );

  function initCacheSync() {
    const cacheKey = parseQueryKey(key);
    const cacheKeyDedupe = `${cacheKey}_dedupe`;

    const cachedState = CACHE.get(cacheKey);

    if (cachedState) {
      Object.assign(state, cachedState);
    }

    return { cacheKey, cacheKeyDedupe };
  }

  async function execute(...args: any[]): Promise<void | AxiosResponse<T>> {
    const { cacheKeyDedupe, cacheKey } = initCacheSync();

    // To eliminate redundant duplicate requests during a close interval
    if (CACHE.get(cacheKeyDedupe)) {
      onSuccess(state.result, ...args);

      return;
    }

    onSettled(...args);

    state.status =
      state.result && key ? READY_STATUS.REVALIDATING : READY_STATUS.PENDING;
    state.error = null;

    try {
      const result = await fetcher(...args);
      state.status = READY_STATUS.SUCCESS;
      state.result = result;
      state.error = null;

      if (cacheKey) {
        CACHE.set(cacheKey, { ...state }, { ttl: cacheTime });
        CACHE.set(cacheKeyDedupe, true, { ttl: staleTime });
      }

      onSuccess(state.result, ...args);

      return result;
    } catch (error) {
      CACHE.delete(cacheKeyDedupe);

      state.status = READY_STATUS.FAILED;

      state.error = (error as AxiosError)?.response?.data;

      onReject(error, ...args);

      throw error;
    }
  }

  watch(
    [shouldFetch, ...reactiveValuesInKey],
    () => {
      if (shouldFetch.value) {
        execute();
      }
    },
    { immediate: true }
  );

  watch(reactiveValuesInKey, () => {
    onKeyUpdated.trigger();
  });

  return {
    execute,
    onKeyUpdated: onKeyUpdated.on,

    isIdle: computed(() => state.status === READY_STATUS.IDLE),
    isError: computed(() => state.status === READY_STATUS.FAILED),
    isSuccess: computed(() => state.status === READY_STATUS.SUCCESS),
    isPending: computed(() => state.status === READY_STATUS.PENDING),
    isRevalidating: computed(() => state.status === READY_STATUS.REVALIDATING),

    ...toRefs(state),
  };
}
